import * as React from 'react'

import {
  BellIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleUserRoundIcon,
  GlobeIcon,
  Grip,
  LogOutIcon,
  MessageSquareTextIcon,
  MoonIcon,
  SettingsIcon,
  SlidersVerticalIcon,
  SunIcon,
  UsersIcon,
} from 'lucide-react'
import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { PresentationScreenKey } from '~/client/graph'
import PurpleConfirm from '~/client/src/desktop/components/PurpleConfirm/PurpleConfirm'
import DesktopInitialState, {
  SidebarComponentName,
} from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import PresentationModeStore from '~/client/src/desktop/stores/ui/PresentationMode.store'
import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import ClockComponent from '~/client/src/shared/components/Clock'
import LanguageSelector from '~/client/src/shared/components/LanguageSelector/LanguageSelector'
import { ProjectLogo } from '~/client/src/shared/components/ProjectLogo/ProjectLogo'
import ProjectSelector, {
  ProjectSelectorTriggerNavBar,
} from '~/client/src/shared/components/ProjectSelector/ProjectSelector'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { getTransitionPath } from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ChatService from '~/client/src/shared/services/ChatService/Chat.service'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { openUserPortal } from '~/client/src/shared/utils/loginPortal'
import { NOOP } from '~/client/src/shared/utils/noop'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'
import { customDebounce } from '~/client/src/shared/utils/util'

import desktopRoutes from '../../constants/desktopRoutes'
import NavBarItem from './components/NavBarItem'
import SettingsItem from './components/SettingsItem'
import CompactWeather from './components/Weather/CompactWeather'
import NavBarStore from './NavBar.store'
import OverflowMenu from './OverflowMenu'
import ResourcesDropdown from './ResourcesDropdown'

import './NavBar.scss'

// localization: translated

interface INavBarProps {
  common?: DesktopCommonStore
  eventsStore?: DesktopEventStore
  projectsStore?: ProjectsStore
  state?: DesktopInitialState
  presentationModeStore?: PresentationModeStore
  projectDateStore?: ProjectDateStore
  notificationsStore?: NotificationsStore
  currentSettingsPage?: string
  chatService?: ChatService
  struxHubAuthService?: StruxHubAuthService
  className?: string
}

interface ILeftMenuItemsData {
  routeLabel: string
  route: string
  isDropdown?: boolean
  dropdownItems?: Array<{ label: string; route: string }>
  testId?: string
}

const MAX_NOTIFICATIONS_BADGE_COUNT = 99
const MENU_SIZE_STEPS_PX = 20
const ALWAYS_COLLAPSE_ALL_AT_PX = 700
const RESOURCES_DROPDOWN_MARKER = 'RESOURCES_DROPDOWN'

@inject(
  'common',
  'eventsStore',
  'projectsStore',
  'state',
  'presentationModeStore',
  'projectDateStore',
  'notificationsStore',
  'chatService',
  'struxHubAuthService',
)
@observer
export default class NavBar extends React.Component<INavBarProps> {
  private readonly navBarStore: NavBarStore

  public constructor(props: INavBarProps) {
    super(props)

    this.navBarStore = new NavBarStore(
      props.common,
      props.eventsStore,
      props.projectsStore,
      props.state,
      props.presentationModeStore,
      props.projectDateStore,
      props.notificationsStore,
    )
  }

  public render() {
    const {
      isPresentationStopped,
      isProjectSelectorDisplayed,
      isProjectSelectorDisabled,
      projects,
      activeProject,
      shouldShowProjectClock,
      logoutHandler,
      hideStopPresentationDialog,
      selectProject,
    } = this.navBarStore
    const { className } = this.props

    return (
      <div
        className={classList({
          'desktop-nav-bar row y-center': true,
          [className]: !!className,
        })}
      >
        <PurpleConfirm
          isShown={isPresentationStopped}
          title={Localization.translator.presentationMode.toUpperCase()}
          actionTitle={Localization.translator.stop}
          className="stop-presentation-confirm"
          onApply={logoutHandler}
          onHide={hideStopPresentationDialog}
          testId="navbar-stop-presentation-dialog"
        >
          {Localization.translator.presentationModeDescriptions.stop}
        </PurpleConfirm>
        <div className="flex-item row no-flex-children x-start y-center no-grow tw:pl-[11px]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                onClick={this.handleClickMyPortal}
                render={
                  // Shared Button; the white-on-dark overrides are needed
                  // because the legacy NavBar has no semantic dark tokens.
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    className="tw:mr-2 tw:shrink-0 tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
                    aria-label={
                      Localization.translator.portalProfile.backToPortal
                    }
                  />
                }
              >
                <Grip className="tw:size-[18px]" />
              </TooltipTrigger>
              <TooltipContent side="right">
                {Localization.translator.portalProfile.backToPortal}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <ProjectLogo
            logoUrl={activeProject?.logoUrl}
            className="logo bg-always-white"
            isLogoWhite={activeProject.isLogoWhite}
            onLogoWasAnalyzedAsMostlyWhite={
              this.handleLogoWasAnalyzedAsMostlyWhite
            }
          />

          <NavBarItem
            isHovering={false}
            onClickHandler={this.stopPropagation}
            navBarStore={this.navBarStore}
            className="pl10"
          >
            {isProjectSelectorDisplayed && (
              <div className="tw:flex tw:min-w-0 tw:items-center">
                <ProjectSelector
                  isDisabled={isProjectSelectorDisabled}
                  isLoading={false}
                  currentProject={activeProject}
                  projects={projects}
                  onItemSelect={selectProject}
                >
                  <ProjectSelectorTriggerNavBar />
                </ProjectSelector>
                {shouldShowProjectClock && (
                  <div className="tw:ml-2 tw:shrink-0 tw:text-zinc-200 tw:hover:text-zinc-200">
                    <ClockComponent
                      format={this.props.projectDateStore.currentFormat.time}
                    />
                  </div>
                )}
              </div>
            )}
          </NavBarItem>
          <NavBarItem
            navBarStore={this.navBarStore}
            onClickHandler={NOOP}
            className="pr10 row"
          >
            <CompactWeather
              className="navbar-item-wrapper"
              onMount={this.handleWindowResize}
            />
            <div className="col project-creation-vertical-line ml12" />
          </NavBarItem>
        </div>
        <>
          <div className="flex-item row x-between y-center text menu-links">
            <div
              className="flex-item row y-center text navbar-list-left flex-grow flex-shrink"
              ref={this.navbarListLeftRef}
            >
              {this.renderLeftMenuItems()}
            </div>
            <div
              className="flex-item no-grow no-shrink row x-end y-center text navbar-list-right mr10"
              ref={this.navbarListRightRef}
            >
              {this.renderRightMenuItems()}
            </div>
          </div>
        </>
      </div>
    )
  }

  public navbarListLeftRef: React.RefObject<HTMLDivElement> = React.createRef()
  public navbarListRightRef: React.RefObject<HTMLDivElement> = React.createRef()

  private handleWindowResize = () => {
    // Set recalculating to true so all items are not hidden for measurement
    this.navBarStore.setRecalculating(true)
    requestAnimationFrame(() => {
      this.updateVisibleCount()
    })
  }

  /**
   * Debounce frequent calls so they smooth out
   */
  private debouncedHandleWindowResize = customDebounce(
    this.handleWindowResize,
    100,
  )

  public componentDidMount() {
    window.addEventListener('resize', this.debouncedHandleWindowResize)
    window.addEventListener('orientationchange', this.handleWindowResize)
    requestAnimationFrame(() => {
      this.updateVisibleCount()
    })
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedHandleWindowResize)
    window.removeEventListener('orientationchange', this.handleWindowResize)
  }

  private updateVisibleCount = () => {
    const container = this.navbarListLeftRef.current
    if (!container) return

    const children = Array.from(container.children)
    let total = 0
    let count = 0

    for (const child of children) {
      // Width can come as 0 if the element is not visible
      // In this case, use a larger than average width of the visible ones for calculation to be on the safe side
      const childWidth =
        (child as HTMLElement).offsetWidth ||
        (total * calculateWidthMultiplier(window.innerWidth)) /
          Math.max(1, count)

      // Calculate width in steps of MENU_SIZE_STEPS_PX to prevent erratic behaviors when navigating
      const w = Math.ceil(childWidth / MENU_SIZE_STEPS_PX) * MENU_SIZE_STEPS_PX
      if (total + w > container.offsetWidth) break

      total += w
      count++
    }

    if (
      count !== this.navBarStore.visibleCount ||
      this.navBarStore.recalculating
    ) {
      this.navBarStore.setVisibleCount(count)
      this.navBarStore.setRecalculating(false)
    }
  }

  private getLeftMenuItemsData(): ILeftMenuItemsData[] {
    const {
      presentationModeStore: { canShowPage },
      state: { isPresentationMode },
    } = this.props

    const {
      isDeliveriesDisabled,
      isLogisticsDisabled,
      isMaterialsDisabled,
      isMaterialResourcesDisabled,
      isEquipmentDisabled,
      isLocationsDisabled,
      isStruxTagsDisabled,
      isPhotosDisabled,
      isFormsDisabled,
      isAnalyticsDisabled,
    } = this.navBarStore
    const items = []
    if (!isLogisticsDisabled && canShowPage(PresentationScreenKey.Logistics))
      items.push({
        routeLabel: Localization.translator.home,
        route: desktopRoutes.HOME,
        testId: 'navbar-home',
      })
    if (!isFormsDisabled && canShowPage(PresentationScreenKey.Forms))
      items.push({
        routeLabel: Localization.translator.workflows,
        route: desktopRoutes.FORMS,
        testId: 'navbar-forms',
      })

    const resourcesDropdownItems = []
    if (!isLocationsDisabled && !isPresentationMode) {
      resourcesDropdownItems.push({
        label: Localization.translator.locations,
        route: desktopRoutes.LOCATIONS,
        testId: 'navbar-locations',
      })
    }
    if (!isEquipmentDisabled && !isPresentationMode) {
      resourcesDropdownItems.push({
        label: Localization.translator.equipment,
        route: desktopRoutes.EQUIPMENT,
        testId: 'navbar-equipment',
      })
    }
    if (!isMaterialResourcesDisabled && !isPresentationMode) {
      resourcesDropdownItems.push({
        label: Localization.translator.materials,
        route: desktopRoutes.MATERIAL_RESOURCES,
        testId: 'navbar-material-resources',
      })
    }
    if (!isStruxTagsDisabled && !isPresentationMode) {
      resourcesDropdownItems.push({
        label: Localization.translator.struxTags,
        route: desktopRoutes.STRUXTAGS,
        testId: 'navbar-struxtags',
      })
    }

    if (resourcesDropdownItems.length > 0) {
      items.push({
        routeLabel: Localization.translator.resources,
        route: RESOURCES_DROPDOWN_MARKER,
        isDropdown: true,
        dropdownItems: resourcesDropdownItems,
        testId: 'navbar-resources',
      })
    }

    if (!isPhotosDisabled && !isPresentationMode)
      items.push({
        routeLabel: Localization.translator.files,
        route: desktopRoutes.FILES,
        testId: 'navbar-files',
      })
    if (!isAnalyticsDisabled && !isPresentationMode)
      items.push({
        routeLabel: Localization.translator.analytics,
        route: desktopRoutes.ANALYTICS,
        testId: 'navbar-analytics',
      })
    if (!isDeliveriesDisabled && canShowPage(PresentationScreenKey.Deliveries))
      items.push({
        routeLabel: Localization.translator.deliveries,
        route: desktopRoutes.DELIVERIES,
        testId: 'navbar-deliveries',
      })
    if (!isMaterialsDisabled && !isPresentationMode)
      items.push({
        routeLabel: Localization.translator.materialsLegacy,
        route: desktopRoutes.MATERIALS,
        testId: 'navbar-materials',
      })

    // Label for when all apps collapse into menu while in Settings
    items['-1'] = {
      routeLabel: Localization.translator.apps,
    }
    return items
  }

  private renderLeftMenuItems(): JSX.Element {
    const { recalculating } = this.navBarStore
    let { visibleCount } = this.navBarStore
    const itemsData = this.getLeftMenuItemsData()

    if (recalculating) {
      const navbarLeft = this.navbarListLeftRef.current
      const navbarRight = this.navbarListRightRef.current
      if (
        navbarRight &&
        navbarRight.offsetWidth +
          navbarLeft.offsetWidth +
          navbarLeft.offsetLeft >
          window.innerWidth - 30 // minus 15px on each side
      ) {
        visibleCount--
      }
    }

    let overflowStart =
      visibleCount >= itemsData.length
        ? visibleCount
        : Math.max(1, visibleCount - 1)

    // If it overflows by only one, considering we need a "More" button, just don't overflow
    if (overflowStart === itemsData.length - 1) {
      overflowStart++
    }

    const collapseAll = window.innerWidth <= ALWAYS_COLLAPSE_ALL_AT_PX
    const currentlyActiveIndex = itemsData.findIndex(data => {
      if (data.isDropdown && data.dropdownItems) {
        return data.dropdownItems.some(item =>
          location.pathname.includes(
            getTransitionPath(
              item.route,
              this.props.state.activeOrInitProjectCode,
            ),
          ),
        )
      }
      return location.pathname.includes(
        getTransitionPath(data.route, this.props.state.activeOrInitProjectCode),
      )
    })

    if (recalculating) {
      setTimeout(() => {
        this.updateVisibleCount()
      })
    }

    // Always render all items. Hide overflowed ones, but during recalculation, unhide them for measurement.
    return (
      <>
        {...itemsData.map((data, idx) => {
          const isOverflowed = collapseAll || idx >= overflowStart
          let extraProps = {}
          if (isOverflowed) {
            if (recalculating) {
              extraProps = {
                className: 'navbar-item--invisible',
                style: {
                  position: 'absolute',
                  pointerEvents: 'none',
                },
              }
            } else {
              extraProps = {
                className: 'navbar-item--hidden',
              }
            }
          }

          if (data.isDropdown && data.dropdownItems) {
            return (
              <ResourcesDropdown
                key={data.route}
                navBarStore={this.navBarStore}
                items={data.dropdownItems}
                testId={data.testId}
                {...extraProps}
              />
            )
          }

          return (
            <NavBarItem
              key={data.route}
              navBarStore={this.navBarStore}
              route={data.route}
              testId={data.testId}
              {...extraProps}
            >
              <div className="navbar-item-wrapper">{data.routeLabel}</div>
            </NavBarItem>
          )
        })}
        {!recalculating &&
          (collapseAll || itemsData.length > overflowStart) && (
            <div className="text large flex-unset">
              <OverflowMenu
                label={
                  collapseAll || currentlyActiveIndex >= overflowStart
                    ? itemsData[currentlyActiveIndex]?.routeLabel
                    : null
                }
                className={classList({
                  active: collapseAll || currentlyActiveIndex >= overflowStart,
                })}
                items={(collapseAll
                  ? itemsData
                  : itemsData.slice(overflowStart)
                ).map((data, idx) => {
                  if (data.isDropdown && data.dropdownItems) {
                    return (
                      <ResourcesDropdown
                        key={data.route}
                        navBarStore={this.navBarStore}
                        items={data.dropdownItems}
                        isInOverflow={true}
                      />
                    )
                  }
                  return (
                    <NavBarItem
                      key={data.route}
                      navBarStore={this.navBarStore}
                      routeLabel={data.routeLabel}
                      route={data.route}
                      icon={
                        idx + overflowStart === currentlyActiveIndex ? (
                          <div className="pl15 pr10 active-check-icon">
                            <CheckIcon size={16} />
                          </div>
                        ) : null
                      }
                      omitIfActive={collapseAll}
                    />
                  )
                })}
              />
            </div>
          )}
      </>
    )
  }

  public get settingsTitle(): React.ReactNode {
    const { isPresentationMode } = this.props.state

    switch (true) {
      case isPresentationMode:
        return Localization.translator.adminSettings

      default:
        return <SettingsIcon size={24} />
    }
  }

  private renderRightMenuItems(): JSX.Element {
    const { state, chatService } = this.props
    const { toggleChat, activeSideBarComponent } = state
    const {
      clickOnNotifications,
      clickOnSettings,
      shouldShowSettingsMenu,
      unreadNotificationsCount,
    } = this.navBarStore

    const hasUnreadNotifications = !!unreadNotificationsCount

    // Cap unread count at 99
    const unreadCount =
      unreadNotificationsCount > MAX_NOTIFICATIONS_BADGE_COUNT
        ? `${MAX_NOTIFICATIONS_BADGE_COUNT}+`
        : unreadNotificationsCount

    return (
      <>
        <NavBarItem
          navBarStore={this.navBarStore}
          onClickHandler={state.toggleSideUserDirectory}
          isActive={
            activeSideBarComponent === SidebarComponentName.UserDirectory
          }
          className="navbar-item-wrapper icon-wrapper mw40 mr4 no-shrink"
          icon={<UsersIcon size={24} />}
          testId="navbar-users-directory"
        />

        <NavBarItem
          navBarStore={this.navBarStore}
          onClickHandler={toggleChat}
          isActive={activeSideBarComponent === SidebarComponentName.Chat}
          className="navbar-item-wrapper icon-wrapper mw40 mr4 no-shrink"
          icon={<MessageSquareTextIcon size={24} />}
          badge={chatService.allUnreadMessagesCount}
          testId="navbar-chat"
        />

        <NavBarItem
          navBarStore={this.navBarStore}
          route={desktopRoutes.NOTIFICATIONS}
          onClickHandler={clickOnNotifications}
          className="navbar-item-wrapper icon-wrapper mw40 mr4 no-shrink"
          icon={<BellIcon size={24} />}
          badge={hasUnreadNotifications && unreadCount}
          testId="navbar-notifications"
        />

        <NavBarItem
          navBarStore={this.navBarStore}
          route={desktopRoutes.PROJECT_SETUP}
          onClickHandler={clickOnSettings}
          isUnclickable={shouldShowSettingsMenu}
          icon={this.settingsTitle}
          className="navbar-item-wrapper icon-wrapper mw40 mr4 no-shrink"
          testId="navbar-settings"
        />
        {this.renderSettingsDropDown()}
      </>
    )
  }

  private renderSettingsDropDown(): JSX.Element {
    const { userActiveProjectSettings } = this.props.state
    const {
      clickOnSettings,
      logoutHandler,
      clickOnProjectSetup,
      clickOnUserSettings,
      shouldShowSettingsMenu,
    } = this.navBarStore

    return (
      <Popover
        open={shouldShowSettingsMenu}
        onOpenChange={open => !open && clickOnSettings()}
      >
        <PopoverContent
          anchor={this.navbarListRightRef}
          side="bottom"
          align="end"
          className="settings tw:w-auto tw:gap-0 tw:px-0 tw:py-1"
        >
          <div className="settings-item wrapper no-hover">
            <div className="compact-user-profile-container my10">
              <UserProfilePreview
                user={this.navBarStore.user}
                avatarSize={AvatarSize.Medium}
                className="x-center y-center"
                shouldSplitToRows
                withContactInfo
                isOnline={null}
              />
            </div>
          </div>

          {Localization.shouldShowLanguageSelector && (
            <SettingsItem
              navBarStore={this.navBarStore}
              onClickHandler={NOOP}
              className="text large"
              icon={<GlobeIcon size={20} />}
              testId="settings-language-selector"
            >
              <LanguageSelector
                icon={<ChevronDownIcon size={12} className="ml4" />}
                className="language-selector"
                onChangeLanguage={this.handleLanguageChange}
              />
            </SettingsItem>
          )}
          {ThemeMode.shouldShowThemeSelector && (
            <SettingsItem
              navBarStore={this.navBarStore}
              onClickHandler={this.handleThemeChange}
              className="text large"
              icon={
                ThemeMode.isDarkMode ? (
                  <MoonIcon size={20} />
                ) : (
                  <SunIcon size={20} />
                )
              }
              testId="settings-theme-selector"
            >
              {ThemeMode.isDarkMode
                ? Localization.translator.themeDark
                : Localization.translator.themeLight}
            </SettingsItem>
          )}

          <div className="bb-light-input-border" />

          <SettingsItem
            navBarStore={this.navBarStore}
            onClickHandler={clickOnUserSettings}
            route={desktopRoutes.PROJECT_SETUP}
            className="nohover text large"
            icon={<CircleUserRoundIcon size={20} />}
            routeLabel={Localization.translator.editProfile}
            testId="settings-edit-profile"
          />

          <SettingsItem
            navBarStore={this.navBarStore}
            onClickHandler={this.handleClickPreferences}
            route={desktopRoutes.PROJECT_SETUP}
            className="nohover text large"
            icon={<SlidersVerticalIcon size={20} />}
            routeLabel={Localization.translator.preferences}
            testId="settings-preferences"
          />

          {userActiveProjectSettings?.isAdmin && (
            <>
              <div className="bb-light-input-border" />
              <SettingsItem
                navBarStore={this.navBarStore}
                onClickHandler={clickOnProjectSetup}
                route={desktopRoutes.PROJECT_SETUP}
                className="nohover text large"
                icon={<SettingsIcon size={20} />}
                routeLabel={Localization.translator.adminSettings}
                testId="settings-admin-settings"
              />
            </>
          )}

          <div className="bb-light-input-border" />

          <SettingsItem
            navBarStore={this.navBarStore}
            onClickHandler={logoutHandler}
            className="nohover text large"
            icon={<LogOutIcon size={20} />}
            routeLabel={Localization.translator.logOut}
            testId="settings-logout"
          />
        </PopoverContent>
      </Popover>
    )
  }

  private handleThemeChange = () => {
    this.navBarStore.clickOnSettings()
    ThemeMode.toggleMode()
  }

  private handleLanguageChange = () => {
    this.navBarStore.clickOnSettings()
    window.dispatchEvent(new Event('resize'))
  }

  private stopPropagation(event: React.SyntheticEvent) {
    event.stopPropagation()
  }

  private handleClickPreferences = () => {
    this.navBarStore.clickOnNotificationPreferences()
    runInAction(() => {
      this.navBarStore.shouldShowSettingsMenu = false
    })
  }

  private handleClickMyPortal = async (event: React.SyntheticEvent) => {
    event.stopPropagation()
    // Full-page navigation to the User Portal on the login host, where the
    // user picks a different project/tenant (switch-tenant). In a preview
    // env this resolves to the matching preview login (see buildPortalUrl).
    await openUserPortal(() => this.props.struxHubAuthService.mintHandoffCode())
  }

  private handleLogoWasAnalyzedAsMostlyWhite = (isMostlyWhite: boolean) => {
    this.navBarStore.saveIsLogoWhite(isMostlyWhite)
  }
}

function calculateWidthMultiplier(w): number {
  const w1 = 1440 // The screen width for the first point
  const x1 = 1 // The desired x value for the first point

  const w2 = 768 // The screen width for the second point
  const x2 = 1.5 // The desired x value for the second point

  // The linear equation is x = mw + c, where m is the slope and c is the y-intercept.

  // The slope (m) is calculated as: m = (x2 - x1) / (w2 - w1)
  const slope = (x2 - x1) / (w2 - w1)

  // The y-intercept (c) is calculated by substituting one of the points into the equation:
  // c = x1 - (slope * w1)
  const intercept = x1 - slope * w1
  let x = slope * w + intercept

  // Constrain outputs
  if (x < x1) {
    x = x1
  } else if (x > x2) {
    x = x2
  }

  return x
}
